import { logger } from '@automata/utils';
import { useCallback, useMemo, useState } from 'react';
import { useUnmount } from 'usehooks-ts';

import { LabwareFields } from './components/LabwareFields';
import { SidebarWrap } from './SidebarWrap';
import {
  deselectNodesEdges,
  setNodeColorIndex,
} from '../../features/graph/graphSlice';
import { selectNodeIdToColorIndex } from '../../features/graph/selectors';
import {
  selectCommentsById,
  selectInstrumentDataById,
  selectLabwareProperties,
} from '../../features/workflow/selectors';
import {
  setComment,
  updateLabwareProperties,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import type { LabwarePropertiesForm } from '../../types';
import {
  createLabwarePropertiesPatch,
  getLabwareColorIndex,
  labwarePropertiesToForm,
} from '../../utils';
import { labwareTypeSchema } from '../../zodSchemas/labwareProperties';
import { validOrFallback } from '../../zodSchemas/utils';

type Props = {
  selectedNodeId: string;
};

/**
 * Use this instead of dispatching updateLabwareProperties directly,
 * so that the dispatched action fully describes the change including related fields.
 */
export function useUpdateLabwareProperties(
  id: string
): (formUpdate: Partial<LabwarePropertiesForm>) => void {
  const dispatch = useAppDispatch();
  const prevProperties = useAppSelector(selectLabwareProperties)[id];

  if (prevProperties === undefined) {
    logger.error(
      `in useUpdateLabwareProperties, id "${id}" does not have properties and cannot be updated`
    );
    return () => {
      logger.error(`cannot update properties for labware ${id}`);
    };
  }

  return (formUpdate) => {
    const propertiesPatch = createLabwarePropertiesPatch(
      formUpdate,
      prevProperties
    );
    dispatch(updateLabwareProperties({ ...propertiesPatch, id }));
  };
}

export const LabwareSidebar = ({
  selectedNodeId,
}: Props): JSX.Element | null => {
  const nodeIdToColorIndex = useAppSelector(selectNodeIdToColorIndex);
  const dispatch = useAppDispatch();
  // Pending changes are submitted on blur, or when this component unmounts.
  const [pendingChanges, setPendingChanges] = useState<
    Partial<LabwarePropertiesForm>
  >({});

  const comment = useAppSelector(selectCommentsById)[selectedNodeId];
  const labwarePropertiesById = useAppSelector(selectLabwareProperties);
  const instrumentsById = useAppSelector(selectInstrumentDataById);

  const instruments = useMemo(
    () => Object.values(instrumentsById),
    [instrumentsById]
  );

  const handleSetComment = useCallback(
    (comment: string) => {
      dispatch(setComment({ id: selectedNodeId, comment }));
    },
    [dispatch, selectedNodeId]
  );

  const commitChanges = useUpdateLabwareProperties(selectedNodeId);

  // can include override for immediate submit, rather than submit on blur.
  // this is useful for checkboxes and other "immediate" inputs.
  const handleSubmitForm = useCallback(
    (override?: Partial<LabwarePropertiesForm>) => {
      const allChanges = { ...pendingChanges, ...override };
      if (Object.keys(allChanges).length > 0) {
        commitChanges(allChanges);
        setPendingChanges({});
      }
    },
    [commitChanges, pendingChanges]
  );

  const handlePendingChange = useCallback(
    (update: Partial<LabwarePropertiesForm>) => {
      setPendingChanges((prev) => ({ ...prev, ...update }));
    },
    []
  );

  useUnmount(() => {
    handleSubmitForm();
  });

  const setLabwareColor = useCallback(
    (colorIndex: number) => {
      dispatch(setNodeColorIndex({ id: selectedNodeId, colorIndex }));
    },
    [dispatch, selectedNodeId]
  );

  const handleCloseSidebar = useCallback(() => {
    dispatch(deselectNodesEdges());
  }, [dispatch]);

  const labwareProperties = labwarePropertiesById[selectedNodeId];

  /** pendingValues are the committed properties, with any pending changes applied. */
  const pendingValues = useMemo((): LabwarePropertiesForm | undefined => {
    if (labwareProperties === undefined) return undefined;

    return {
      ...labwarePropertiesToForm(labwareProperties),
      ...pendingChanges,
    };
  }, [labwareProperties, pendingChanges]);

  if (labwareProperties === undefined || pendingValues === undefined) {
    logger.error(`no labware properties for node ${selectedNodeId}`);
    return null;
  }

  const labwareColorIndex = getLabwareColorIndex({
    id: selectedNodeId,
    nodeIdToColorIndex,
    labwareType: validOrFallback(
      labwareTypeSchema.safeParse(pendingValues.labwareType),
      null
    ),
  });

  // HACK(IL, 2024-04-04): using 'key' to ensure that semicontrolled fields don't
  // do a late update on blur/unmount causing zombie form updates (see WBP-125)
  return (
    <SidebarWrap
      key={selectedNodeId}
      comment={comment}
      handleSetComment={handleSetComment}
      handleCloseSidebar={handleCloseSidebar}
    >
      <LabwareFields
        labwareId={selectedNodeId}
        labwareColorIndex={labwareColorIndex}
        setLabwareColor={setLabwareColor}
        handlePendingChange={handlePendingChange}
        handleSubmitForm={handleSubmitForm}
        // TODO: this "instruments" array should have incompatible labware filtered out (but labware compatibility is not implemented),
        // vs the one for the TaskPropertiesSidebar should have instruments that don't match the taskType filtered out.
        instruments={instruments}
        values={pendingValues}
      />
    </SidebarWrap>
  );
};
